// The manuals offline pack - the pure part (src/lib/manualOffline.js wires it to the browser; tests/manuals-2.test.mjs runs it):
// which manuals go offline (pickOfflineManuals) and building the pack into a cache (buildPack, every dependency passed in).
import { isContingency } from './contingency.js'
import { sopSections, normaliseSop } from './sopStandard.js'
import { sopImagePaths } from './sopVisual.js'

export const CACHE = 'hc-manuals-v1'
export const MAX_FILES = 30
export const MAX_BYTES = 200 * 1024 * 1024
export const REFRESH_MS = 6 * 3600 * 1000
export const EMERGENCY_RX = /emergenc|contingenc|abandon|fire|flood|man overboard|\bmob\b|collision|grounding|evacuat|pollution|oil spill|sopep|medical|first aid|security|piracy|blackout|steering failure|search and rescue|\bsar\b|distress/i
export const LSA_RX = /\blsa\b|\bffe\b|life ?saving|fire ?fighting|life ?raft|liferaft|life ?jacket|lifebuoy|immersion suit|epirb|\bsart\b|\bplb\b|rescue boat|davit|fire extinguish|extinguisher|fire detect|fire alarm|sprinkler|fixed fire|co2 system|fm-?200|novec|breathing apparatus|\beebd\b|fire pump|fire hose|fire blanket/i
export const SMS_CATEGORY = 'SMS / ISM'

const approved = m => m && m.status === 'approved' && !m.archived_at
const generated = m => m && m.manual_type !== 'uploaded' && !m.file_url && Array.isArray(m.sections) && m.sections.length > 0

// which manuals go offline and why. manuals / links / checklists / equipment = rows the login can read.
// -> [{ id, title, version, file_url, file_name, reasons: [..], sections: [{ heading, body }] | null }]
export function pickOfflineManuals({ manuals, links, checklists, equipment }) {
  const plans = new Map((checklists || []).filter(isContingency).map(c => [String(c.id), c.title]))
  const lsaEq = new Map((equipment || []).filter(e => LSA_RX.test([e.name, e.system].filter(Boolean).join(' '))).map(e => [String(e.uid), e.name]))
  const out = []
  for (const m of (manuals || []).filter(approved)) {
    // an approved SOP (sql/sop-layer.sql): always offline, as its text in the standard (small; the original file of an import stays online)
    if (m.doc_kind === 'sop') { out.push({ id: m.id, title: [m.doc_number, m.title].filter(Boolean).join(' '), version: m.version || 1, file_url: null, file_name: null, reasons: ['SOP'], sections: sopSections(m.sop, m), sop: normaliseSop(m.sop), media: sopImagePaths(m.sop) }); continue }   // sop-visual: the SOP itself (Do it works offline) + its photos
    const reasons = []
    const mine = (links || []).filter(l => l.manual_id === m.id)
    for (const l of mine) if (l.kind === 'checklist' && plans.has(String(l.target_id))) reasons.push('Contingency plan: ' + plans.get(String(l.target_id)))
    const lsa = LSA_RX.test([m.title, m.category, m.file_name].filter(Boolean).join(' ')) || mine.some(l => l.kind === 'equipment' && lsaEq.has(String(l.target_id)))
    if (lsa) reasons.push('LSA / FFE')
    let sections = null
    const sms = m.category === SMS_CATEGORY || generated(m)
    if (sms && generated(m)) {
      const em = m.sections.filter(x => EMERGENCY_RX.test(String(x.heading || x.title || '')))
      if (em.length) { sections = em.map(x => ({ heading: String(x.heading || x.title || ''), body: String(x.body || x.text || '') })); reasons.push('SMS emergency sections') }
    } else if (sms && m.file_url && EMERGENCY_RX.test(m.title || '')) reasons.push('SMS emergency procedures')
    if (!reasons.length) continue
    // a generated manual: the text goes offline (only its emergency sections, or all of it when it is linked to a plan / LSA)
    if (generated(m) && !sections) sections = m.sections.map(x => ({ heading: String(x.heading || x.title || ''), body: String(x.body || x.text || '') }))
    out.push({ id: m.id, title: m.title, version: m.version || 1, file_url: m.file_url || null, file_name: m.file_name || null, reasons: [...new Set(reasons)], sections })
  }
  // contingency plans first, then LSA / FFE, then SMS; at most MAX_FILES files
  const rank = x => (x.reasons.some(r => r.startsWith('Contingency')) ? 0 : x.reasons.includes('LSA / FFE') ? 1 : x.reasons.includes('SOP') ? 2 : 3)
  out.sort((a, b) => rank(a) - rank(b) || String(a.title).localeCompare(String(b.title)))
  let files = 0
  return out.filter(x => !x.file_url || ++files <= MAX_FILES)
}

export const keyOf = (uid, id, v) => '/__hc_manual/' + uid + '/' + id + '/v' + v
// a photo of an approved SOP (sop-visual): one cache entry per path
export const mediaKey = (uid, path) => '/__hc_manual/' + uid + '/media/' + encodeURIComponent(path)
export const indexKey = uid => '/__hc_manual/' + uid + '/index.json'
const pathOf = k => new URL(k.url || k, 'https://x').pathname
export async function wipeUser(cache, uid) { for (const k of await cache.keys()) if (pathOf(k).startsWith('/__hc_manual/' + uid + '/')) await cache.delete(k) }

// build / refresh the pack into `cache` (online only): db = a Supabase client, fetchFile(url, id) -> Response | null, now(), online
export async function buildPack({ uid, clientId, rightsHash = '', force = false, db, fetchFile, fetchMedia, cache, now = () => Date.now(), online = true }) {
  if (!uid || !clientId || !cache) return { skipped: 'no storage' }
  const t = now()
  const old = await cache.match(indexKey(uid)).then(r => (r ? r.json() : null)).catch(() => null)
  if (old && old.rights !== rightsHash) await wipeUser(cache, uid)   // rights changed: nothing kept from before
  else if (!force && old && old.vessel === clientId && t - old.at < REFRESH_MS) return { skipped: 'fresh', pack: old }
  if (!online) return { skipped: 'offline', pack: old && old.rights === rightsHash && old.vessel === clientId ? old : null }
  // the same read as the Manuals page (so its list is in the offline cache too)
  const { data: manuals, error } = await db.from('manuals').select('*').eq('client_id', clientId).order('updated_at', { ascending: false })
  if (error) return { error: error.message }
  const [{ data: links }, { data: checklists }, { data: equipment }] = await Promise.all([
    db.from('manual_links').select('manual_id,kind,target_id').eq('client_id', clientId).in('kind', ['checklist', 'equipment']),
    db.from('checklists').select('id,title,category').eq('client_id', clientId),
    db.from('equipment').select('uid,name,system').eq('client_id', clientId),
  ])
  const items = pickOfflineManuals({ manuals, links, checklists, equipment })
  const keep = new Set([indexKey(uid)])
  let bytes = 0
  for (const it of items) {
    if (!it.file_url) continue
    const k = keyOf(uid, it.id, it.version)
    const have = await cache.match(k)
    if (have) { const b = await have.clone().blob(); bytes += b.size; it.cached = true; it.size = b.size; keep.add(k); continue }
    try {
      const r = await fetchFile(it.file_url, it.id)
      if (!r) { it.error = 'could not be downloaded'; continue }
      const blob = await r.blob()
      if (bytes + blob.size > MAX_BYTES) { it.error = 'too large for the offline pack'; continue }
      await cache.put(k, new Response(blob, { headers: { 'content-type': blob.type || 'application/pdf' } }))
      bytes += blob.size; it.cached = true; it.size = blob.size; keep.add(k)
    } catch (e) { it.error = 'could not be downloaded' }
  }
  // sop-visual: the photos of the approved SOPs in the pack (images only; a video needs a connection). Same size cap as the files.
  for (const it of items) {
    if (!it.media || !it.media.length) continue
    it.media_cached = 0
    for (const path of it.media) {
      const k = mediaKey(uid, path)
      const have = await cache.match(k)
      if (have) { const b = await have.clone().blob(); bytes += b.size; keep.add(k); it.media_cached++; continue }
      if (!fetchMedia) continue
      try {
        const r = await fetchMedia(path)
        if (!r) continue
        const blob = await r.blob()
        if (bytes + blob.size > MAX_BYTES) { it.error = 'some photos are too large for the offline pack'; continue }
        await cache.put(k, new Response(blob, { headers: { 'content-type': blob.type || 'image/jpeg' } }))
        bytes += blob.size; keep.add(k); it.media_cached++
      } catch (e) { it.error = 'some photos could not be downloaded' }
    }
  }
  // out: files no longer in the pack (archived, replaced by a newer version, no longer visible / linked)
  for (const k of await cache.keys()) { const p = pathOf(k); if (p.startsWith('/__hc_manual/' + uid + '/') && !keep.has(p)) await cache.delete(k) }
  const pack = { at: t, rights: rightsHash, vessel: clientId, bytes, items }
  await cache.put(indexKey(uid), new Response(JSON.stringify(pack), { headers: { 'content-type': 'application/json' } }))
  return { pack }
}
